import { TEAM_FIELD_HIDDEN_FROM_SCOREBOARD } from '../constants';

export const TEAM_FILTER_HIDDEN_FROM_SCOREBOARD = (team) => !(team?.team?.[TEAM_FIELD_HIDDEN_FROM_SCOREBOARD] ?? false);
export const TEAM_FILTER_POSITIVE_SCORES_ONLY = (team) => team.total > 0;
export const PREDICATE_INCLUDE_ALL = () => true;

export default class ScoringApiClient {

  constructor(api) {
    // reference to Api class in `apps/boss-admin/src/api.js` and `apps/boss-client/src/api.js`
    // TODO: Once Api class is moved to shared boss-ui package, can remove this dependency
    this.api = api;
  }

  abortCurrentRequest() {
    this.api.abortCurrentRequest();
  }

  async getEventDashboard({
                            eventId,
                            displayPageSize = 20,
                            apiPageSize = 20,
                            lastEvaluatedKey = {},
                            includeHiddenTeams = false,
                            positiveScoresOnly = false
                          }) {
    const hiddenTeamFilter = includeHiddenTeams ? PREDICATE_INCLUDE_ALL : TEAM_FILTER_HIDDEN_FROM_SCOREBOARD;
    const scoreFilter = positiveScoresOnly ? TEAM_FILTER_POSITIVE_SCORES_ONLY : PREDICATE_INCLUDE_ALL;
    const filterFunction = (team) => hiddenTeamFilter(team) && scoreFilter(team);

    return this.getEventDashboardWithFilters({
      eventId,
      displayPageSize,
      apiPageSize,
      lastEvaluatedKey,
      filterFunction
    });
  }

  /**
   * @callback filterFunction
   * @param {object} team - The team object to filter.
   * @returns {boolean} - True if the team should be included in the results, false otherwise.
   */

  /**
   * Get event scoring dashboard data and perform client-side filtering.
   *
   * Tries to return filtered items to meet the expected displayPageSize if possible
   *
   * @param {string} eventId - Event ID
   * @param {number} displayPageSize - the desired number of items to display on the page
   * @param {number} apiPageSize - the number of items to fetch from the API per request
   * @param {object} lastEvaluatedKey - Optional DynamoDB pagination key.
   * @param {filterFunction} filterFunction - Function to filter teams
   * @returns {Promise<{pagination: {more: boolean, LastEvaluatedKey: Object}, teams: Object[]}>}
   * @example
   * const { pagination, teams } = await getEventDashboardWithFilters({
   *    eventId: '3hmf7oEl6OrJKbPXwPl5oB',
   *    displayPageSize: 20,
   *    apiPageSize: 20,
   *    lastEvaluatedKey: {
   *     "GSI2PK": "EVENT#3hmf7oEl6OrJKbPXwPl5oB#DASHBOARD",
   *     "partitionKey": "EVENT#3hmf7oEl6OrJKbPXwPl5oB#DASHBOARD",
   *     "sortKey": "TEAM#teamname",
   *     "GSI2SK": 500
   *    },
   *    filterFunction: (team) => team.total > 0
   *  });
   */
  async getEventDashboardWithFilters({
                                       eventId,
                                       displayPageSize = 20,
                                       apiPageSize = 20,
                                       lastEvaluatedKey = {},
                                       filterFunction
                                     }) {
    if (!eventId || typeof eventId !== 'string') {
      throw new Error('string argument eventId is required');
    }

    if (!Number.isInteger(displayPageSize) || displayPageSize <= 0) {
      throw new Error('argument displayPageSize must be a positive integer');
    }

    if (!Number.isInteger(apiPageSize) || apiPageSize <= 0) {
      throw new Error('argument apiPageSize must be a positive integer');
    }

    if (typeof lastEvaluatedKey !== 'object') {
      throw new Error('argument lastEvaluatedKey must be an object');
    }

    if (typeof filterFunction !== 'function') {
      throw new Error('argument filterFunction must be a function');
    }

    let exclusiveStartKey = { ...lastEvaluatedKey };
    const allItems = [];
    let hasMore = true;
    while (hasMore && allItems.length < displayPageSize) {
      const queryStringParameters = {
        pageSize: apiPageSize,
        ...exclusiveStartKey
      };
      const { pagination, teams } = await this.api.get('events', `/events/${eventId}/dashboard`, {
        queryStringParameters
      });
      hasMore = pagination?.more ?? false;
      exclusiveStartKey = pagination?.LastEvaluatedKey ?? {};
      const filteredTeams = teams.filter((item) => filterFunction(item));
      allItems.push(...filteredTeams);
    }
    const responsePaginationMore = hasMore || allItems.length > displayPageSize;

    // If we have more items than we need, splice the array to length displayPageSize
    if (allItems.length > displayPageSize) {
      allItems.splice(displayPageSize);
    }
    let lastItemKey = {};
    if (allItems.length > 0) {
      const lastItem = allItems[allItems.length - 1];
      // return keys of last filtered item
      lastItemKey = {
        partitionKey: lastItem.partitionKey,
        sortKey: lastItem.sortKey,
        GSI2PK: lastItem.GSI2PK,
        GSI2SK: lastItem.GSI2SK
      };
    }
    const responsePagination = {
      more: responsePaginationMore,
      LastEvaluatedKey: lastItemKey
    };
    return { pagination: responsePagination, teams: allItems };
  }
}